/**
 * Who the onboarding form's trusted contact is (crossmarc/steward-app#1954).
 *
 * The trusted contact used to be four free-text fields, so a spouse who was also the trusted
 * contact existed twice once setup ran: as the spouse's `person` row and as an `other` contact.
 * Matt ruled (2026-09-30, "Any adult member please") that the client can instead name their
 * spouse or partner, or any child who is 18 or older, as the trusted contact.
 *
 * ## The stored choice
 *
 * `trustedContact_member` in the `trustedContact` section: `'spouse'`, or `'child:<index>'`
 * (an index into the index-aligned `children_names` / `children_dateOfBirths` lists), or absent
 * meaning "someone else". It is only a POINTER into other answers, and those answers live in
 * other sections that can change after the pointer was saved. So no reader trusts the pointer:
 * every reader (the form, the section status, the review page, the firm's details model and the
 * setup prefill) goes through {@link resolveTrustedContact}, which re-checks it against the
 * answers as they stand now and falls back to the free-text fields when it no longer holds.
 *
 * ## Where each detail comes from
 *
 * - spouse: name from `spouse_firstName` / `spouse_middleName` / `spouse_lastName`, phone and
 *   email from `spouse_phone` / `spouse_email` (the Your family page owns them).
 * - child: name from `children_names[i]`; a child has no phone or email on the family page, so
 *   the form asks for them and they are stored in the existing `trustedContact_phone` /
 *   `trustedContact_email` keys.
 * - someone else: the four `trustedContact_*` free-text keys, as before.
 *
 * Setup points the self row at the family member's `person` row and writes no contact row. That
 * person row carries the phone and email this resolver returns (crossmarc/steward-app#1957): the
 * spouse's own answers, or the child's `trustedContact_*` answers (`intakePrefill.ts`).
 */

export const ONBOARDING_TRUSTED_MEMBER_KEY = 'trustedContact_member';
/**
 * The chosen child's name as entered when the choice was made. `child:<n>` alone is positional, so
 * removing an earlier child would silently re-point it at the next child (and the phone and email
 * typed for the first). The resolver accepts a child choice only while the child at <n> still has
 * this name (trimmed, case-insensitive).
 */
export const ONBOARDING_TRUSTED_MEMBER_NAME_KEY = 'trustedContact_memberName';
/** Keys the form never renders as ordinary fields: they carry the choice, not an answer. */
export const ONBOARDING_TRUSTED_CHOICE_KEYS: readonly string[] = [ONBOARDING_TRUSTED_MEMBER_KEY, ONBOARDING_TRUSTED_MEMBER_NAME_KEY];

export const TRUSTED_RELATIONSHIP_SPOUSE = 'Spouse or partner';
export const TRUSTED_RELATIONSHIP_CHILD = 'Child';

/** The age at which a child can be the trusted contact. */
export const TRUSTED_CONTACT_MIN_AGE = 18;

export interface ResolvedTrustedContact {
  subject: 'spouse' | 'child' | 'other';
  /** Empty when the answer is missing (only possible for `other`, a family member always has one). */
  name: string;
  /** "Spouse or partner", "Child", or the client's own words for `other`. */
  relationship: string;
  phone: string;
  email: string;
  /** Set for `child`: the position in `children_names` / `children_dateOfBirths`. */
  childIndex?: number;
}

type Bag = Readonly<Record<string, unknown>>;

const text = (bag: Bag, key: string): string => {
  const v = bag[key];
  return typeof v === 'string' ? v.trim() : '';
};

const list = (bag: Bag, key: string): string[] => {
  const v = bag[key];
  return Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? x.trim() : '')) : [];
};

const sameName = (x: string, y: string): boolean => x !== '' && x.toLowerCase() === y.toLowerCase();

/** A `YYYY-MM-DD` date that is a real calendar day, or null. */
function calendarDay(v: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!match) return null;
  const y = Number(match[1]); const m = Number(match[2]); const d = Number(match[3]);
  const parsed = new Date(Date.UTC(y, m - 1, d));
  return parsed.getUTCFullYear() === y && parsed.getUTCMonth() === m - 1 && parsed.getUTCDate() === d ? { y, m, d } : null;
}

const todayString = (today: Date | string | undefined): string => {
  if (typeof today === 'string') return today;
  const t = today ?? new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`;
};

/**
 * True when `dateOfBirth` is a real `YYYY-MM-DD` date and the person has turned
 * {@link TRUSTED_CONTACT_MIN_AGE} on or before `today` (a local calendar date, `YYYY-MM-DD`, or a
 * Date read in local time; default now). A February 29 birthday counts from March 1 in a year with
 * no 29th, by string order.
 */
export function isAdultOn(dateOfBirth: string, today?: Date | string): boolean {
  const dob = calendarDay(dateOfBirth.trim());
  if (!dob) return false;
  const p = (n: number) => String(n).padStart(2, '0');
  const birthday = `${dob.y + TRUSTED_CONTACT_MIN_AGE}-${p(dob.m)}-${p(dob.d)}`;
  return birthday <= todayString(today);
}

/**
 * True only when `dateOfBirth` (or an ISO datetime whose first 10 characters are) is a real `YYYY-MM-DD` date that shows the person is still under
 * {@link TRUSTED_CONTACT_MIN_AGE} on `today`. A missing or unreadable date is NOT under 18 here:
 * it means "not known", which callers that must stay permissive (the Family picker) rely on.
 */
export function isUnderageOn(dateOfBirth: string | undefined, today?: Date | string): boolean {
  // An ISO datetime ('2012-06-07T00:00:00Z') counts by its date part.
  const v = (dateOfBirth ?? '').trim().slice(0, 10);
  return calendarDay(v) !== null && !isAdultOn(v, today);
}

/** The children who could be the trusted contact: a name, and a real date of birth that is 18 or older. */
export function adultChildIndexes(answers: Bag, today?: Date | string): number[] {
  const names = list(answers, 'children_names');
  const dobs = list(answers, 'children_dateOfBirths');
  const out: number[] = [];
  for (let i = 0; i < names.length; i++) {
    if (names[i] !== '' && isAdultOn(dobs[i] ?? '', today)) out.push(i);
  }
  return out;
}

/** The spouse's name as the form's Your family page has it, or '' when there is no spouse to choose. */
export function trustedSpouseName(answers: Bag): string {
  if (answers['spouse_included'] !== true) return '';
  const first = text(answers, 'spouse_firstName');
  const last = text(answers, 'spouse_lastName');
  // A middle name alone is not a name: setup names the spouse's row from first or last (and
  // falls back to the snapshot's otherwise), so this must agree or the pointer would name a row
  // that is not the one the client chose.
  if (first === '' && last === '') return '';
  return [first, text(answers, 'spouse_middleName'), last].filter(Boolean).join(' ');
}

/** The choice as stored, when it has the right shape. It is NOT yet known to still be valid. */
export function parseTrustedMember(v: unknown): { kind: 'spouse' } | { kind: 'child'; index: number } | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (t === 'spouse') return { kind: 'spouse' };
  const m = /^child:(0|[1-9]\d{0,2})$/.exec(t);
  return m ? { kind: 'child', index: Number(m[1]) } : null;
}

/** The stored value for a choice; the inverse of {@link parseTrustedMember}. */
export const trustedMemberValue = (c: { kind: 'spouse' } | { kind: 'child'; index: number }): string =>
  c.kind === 'spouse' ? 'spouse' : `child:${c.index}`;

/**
 * The trusted contact for `answers` (merged saved answers, or the firm's read of the row), or
 * null when there is none at all. `today` is only for the 18-or-older test; pass it to pin a date.
 *
 * A family member resolves only while the choice still holds: the spouse only when
 * `spouse_included` is true and there is a spouse name, a child only when the index names a child
 * with a name and a real date of birth that is 18 or older today. Otherwise the answer falls back
 * to the free-text fields, as if no member had been chosen.
 */
export function resolveTrustedContact(answers: Bag | null | undefined, today?: Date | string): ResolvedTrustedContact | null {
  const a: Bag = answers ?? {};
  const member = parseTrustedMember(a[ONBOARDING_TRUSTED_MEMBER_KEY]);
  if (member?.kind === 'spouse') {
    const name = trustedSpouseName(a);
    if (name !== '') {
      return {
        subject: 'spouse', name, relationship: TRUSTED_RELATIONSHIP_SPOUSE,
        phone: text(a, 'spouse_phone'), email: text(a, 'spouse_email'),
      };
    }
  } else if (member?.kind === 'child' && adultChildIndexes(a, today).includes(member.index)
    && sameName(text(a, ONBOARDING_TRUSTED_MEMBER_NAME_KEY), list(a, 'children_names')[member.index]!)) {
    return {
      subject: 'child', name: list(a, 'children_names')[member.index]!, relationship: TRUSTED_RELATIONSHIP_CHILD,
      phone: text(a, 'trustedContact_phone'), email: text(a, 'trustedContact_email'), childIndex: member.index,
    };
  }
  // A child choice that no longer holds leaves the phone and email typed for THAT child behind
  // (the name and relationship were dropped when the child was chosen). They are not the
  // "someone else" contact's, so they are blanked rather than shown under a person they do not
  // belong to. Any other fallback keeps them.
  const staleChild = member?.kind === 'child';
  const other: ResolvedTrustedContact = {
    subject: 'other', name: text(a, 'trustedContact_name'), relationship: text(a, 'trustedContact_relationship'),
    phone: staleChild ? '' : text(a, 'trustedContact_phone'), email: staleChild ? '' : text(a, 'trustedContact_email'),
  };
  return other.name || other.relationship || other.phone || other.email ? other : null;
}

/**
 * What a section save of `trustedContact` sends for the choice: the `trustedContact_*` keys
 * with the ones a chosen family member makes stale removed, and the member key only while it
 * still resolves. A spouse choice drops all four free-text keys (the spouse's own phone and email
 * are read from the family page); a child choice drops the name and relationship but keeps the
 * phone and email typed for that child; anything else drops the member key.
 */
export function trustedContactPayload(
  payload: Record<string, string | boolean | string[]>,
  answers: Bag,
  today?: Date | string,
): Record<string, string | boolean | string[]> {
  const out = { ...payload };
  const r = resolveTrustedContact(answers, today);
  if (r?.subject === 'spouse' || r?.subject === 'child') {
    out[ONBOARDING_TRUSTED_MEMBER_KEY] = r.subject === 'spouse' ? 'spouse' : trustedMemberValue({ kind: 'child', index: r.childIndex! });
    if (r.subject === 'child') out[ONBOARDING_TRUSTED_MEMBER_NAME_KEY] = r.name; else delete out[ONBOARDING_TRUSTED_MEMBER_NAME_KEY];
    delete out['trustedContact_name'];
    delete out['trustedContact_relationship'];
    if (r.subject === 'spouse') { delete out['trustedContact_phone']; delete out['trustedContact_email']; }
  } else {
    // A child choice that no longer holds also leaves that child's phone and email behind:
    // they are not sent (a blank clears), matching what the resolver reads.
    if (parseTrustedMember(answers[ONBOARDING_TRUSTED_MEMBER_KEY])?.kind === 'child') {
      delete out['trustedContact_phone']; delete out['trustedContact_email'];
    }
    delete out[ONBOARDING_TRUSTED_MEMBER_KEY];
    delete out[ONBOARDING_TRUSTED_MEMBER_NAME_KEY];
  }
  return out;
}
